import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Check } from "lucide-react";
import { MAT_FX, CONNECTOR_FX } from "./toothUtils";

const MATERIALS = [
  {
    v: "metal_porcelan",
    name: "Metal-Porcelan",
    tag: "Zgjidhja klasike ekonomike",
    desc: "Bërthamë metalike e veshur me porcelan. Shumë rezistente, por me kohë mund të dallohet vija gri e metalit te mishi i dhëmbëve.",
    estetika: 3, fortesia: 4, cmimi: 1,
    best: "Dhëmbët e pasmë",
    badge: "#6B7280",
  },
  {
    v: "zirkon",
    name: "Zirkon",
    tag: "Fortësi maksimale, pa metal",
    desc: "E bardhë kompakte, pa asnjë metal — nuk krijon kurrë vijë gri te mishi. Materiali më i fortë, ideal për ura dhe dhëmbë të pasmë.",
    estetika: 4, fortesia: 5, cmimi: 2,
    best: "Ura & dhëmbët e pasmë",
    badge: "#0E7490",
  },
  {
    v: "zirkon_emax",
    name: "Zirkon E-max",
    tag: "Estetika më natyrale",
    desc: "Qeramikë e tejdukshme që e përcjell dritën si smalti natyral — praktikisht e padallueshme nga dhëmbët tuaj. Zgjedhja premium për buzëqeshjen.",
    estetika: 5, fortesia: 4, cmimi: 3,
    best: "Dhëmbët e përparmë",
    badge: "#7C3AED",
  },
];

// ── Pamje paraprake me dhëmbët REALË të odontogramës (sprite + filtër materiali) ──
const TW = 64, TH = 150;
const PREVIEW_TEETH = [
  { src: "/teeth/incisor-upper.png", aspect: 0.33, natural: true },
  { src: "/teeth/incisor-upper.png", aspect: 0.33 },
  { src: "/teeth/canine.png", aspect: 0.3025 },
];

function toothGeom(aspect) {
  const drawnH = Math.min(TH - 8, (TW - 4) / aspect);
  const drawnW = drawnH * aspect;
  return { drawnH, drawnW, dx: (TW - drawnW) / 2, dy: 6, cervY: 6 + drawnH * 0.42 };
}

function PreviewTooth({ x, src, aspect, mat, uid }) {
  const { drawnH, drawnW, dx, dy, cervY } = toothGeom(aspect);
  const cx = TW / 2;
  return (
    <g transform={`translate(${x},0)`}>
      <g transform={`translate(0,${TH}) scale(1,-1)`}>
        <image href={src} x={dx} y={dy} width={drawnW} height={drawnH} />
        {mat && (
          <>
            <image href={src} x={dx} y={dy} width={drawnW} height={drawnH} clipPath={`url(#pvclip-${uid})`} filter={`url(#pvfx-${mat})`} />
            {mat === "metal_porcelan" ? (
              <rect x={dx + drawnW * 0.1} y={cervY - 4} width={drawnW * 0.8} height={4.5} rx={2} fill="#7C8590" />
            ) : (
              <path d={`M ${dx + drawnW * 0.08} ${cervY - 1} Q ${cx} ${cervY + 1.5} ${dx + drawnW * 0.92} ${cervY - 1}`}
                    stroke={mat === "zirkon" ? "#AEB9C2" : "#C4B491"} strokeWidth="1.4" fill="none" opacity="0.9" />
            )}
            {mat === "zirkon_emax" && (
              <path d={`M ${cx - drawnW * 0.22} ${dy + drawnH * 0.1} Q ${cx} ${dy + drawnH * 0.04} ${cx + drawnW * 0.22} ${dy + drawnH * 0.1}`}
                    stroke="#FFFFFF" strokeWidth="2.2" fill="none" opacity="0.75" strokeLinecap="round" />
            )}
          </>
        )}
      </g>
    </g>
  );
}

function MaterialPreview({ mat }) {
  const fx = MAT_FX[mat];
  const cs = CONNECTOR_FX[mat];
  const barTopSrc = 6 + toothGeom(0.33).drawnH * 0.13;
  const barY = TH - barTopSrc - 9;
  return (
    <div className="rounded-lg border border-[#E2DFD8] bg-[#FDFBF7] px-2 pt-2 pb-1" data-testid={`material-preview-${mat}`}>
      <svg viewBox="0 0 198 150" className="w-full h-40">
        <defs>
          <filter id={`pvfx-${mat}`} colorInterpolationFilters="sRGB">
            <feColorMatrix type="saturate" values={`${fx.sat}`} />
            <feComponentTransfer>
              <feFuncR type="linear" slope={fx.slope} intercept={fx.intercept} />
              <feFuncG type="linear" slope={fx.slope} intercept={fx.intercept} />
              <feFuncB type="linear" slope={fx.slope} intercept={fx.intercept} />
            </feComponentTransfer>
          </filter>
          {PREVIEW_TEETH.map((t, i) => !t.natural && (
            <clipPath key={i} id={`pvclip-${mat}-${i}`}>
              <rect x="0" y="0" width={TW} height={toothGeom(t.aspect).cervY} />
            </clipPath>
          ))}
        </defs>
        <rect x={99} y={barY} width={66} height={9} rx={3.5} fill={cs.fill} stroke={cs.stroke} strokeWidth="1" />
        {PREVIEW_TEETH.map((t, i) => (
          <PreviewTooth key={i} x={i * 66 + 1} src={t.src} aspect={t.aspect} mat={t.natural ? "" : mat} uid={`${mat}-${i}`} />
        ))}
      </svg>
      <div className="flex justify-between px-1 text-[10px] font-semibold uppercase tracking-wide">
        <span className="text-[#8A8A8A]">Natyral</span>
        <span style={{ color: MATERIALS.find((m) => m.v === mat)?.badge }}>Me kurorë</span>
      </div>
    </div>
  );
}

const Dots = ({ n, max = 5, color }) => (
  <span className="inline-flex gap-0.5">
    {Array.from({ length: max }).map((_, i) => (
      <span key={i} className="inline-block h-2 w-2 rounded-full" style={{ background: i < n ? color : "#E2DFD8" }} />
    ))}
  </span>
);

export default function CrownMaterialCompare({ open, onClose, onPick }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="material-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#7C3AED]" />
            Si do duken dhëmbët? — Krahasimi i materialeve
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#5C5C5C] -mt-1">
          Krahasoni në dhëmbët e odontogramës: majtas dhëmbi natyral, djathtas si do duket me kurorën e secilit material.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-2">
          {MATERIALS.map((m) => (
            <div key={m.v} className="rounded-xl border border-[#E2DFD8] overflow-hidden flex flex-col p-3" data-testid={`material-card-${m.v}`}>
              <MaterialPreview mat={m.v} />
              <div className="pt-3 flex flex-col flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-base">{m.name}</h4>
                  <span className="rounded-full px-2 py-0.5 text-[9px] font-bold text-white uppercase tracking-wide" style={{ background: m.badge }}>
                    {m.best}
                  </span>
                </div>
                <div className="text-xs font-medium mt-0.5" style={{ color: m.badge }}>{m.tag}</div>
                <p className="text-xs text-[#5C5C5C] mt-2 leading-relaxed">{m.desc}</p>
                <div className="mt-3 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between"><span className="text-[#5C5C5C]">Estetika</span><Dots n={m.estetika} color={m.badge} /></div>
                  <div className="flex items-center justify-between"><span className="text-[#5C5C5C]">Fortësia</span><Dots n={m.fortesia} color={m.badge} /></div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#5C5C5C]">Çmimi</span>
                    <span className="font-bold tracking-widest" style={{ color: m.badge }}>{"€".repeat(m.cmimi)}<span className="text-[#E2DFD8]">{"€".repeat(3 - m.cmimi)}</span></span>
                  </div>
                </div>
                {onPick && (
                  <button
                    type="button"
                    onClick={() => onPick(m.v)}
                    data-testid={`material-pick-${m.v}`}
                    className="mt-4 w-full rounded-lg border-2 py-2 text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                    style={{ borderColor: m.badge, color: m.badge }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = m.badge; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = m.badge; }}
                  >
                    <Check size={14} /> Zgjidh këtë material
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
